import { Injectable } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { Observable } from 'rxjs';
import { take } from 'rxjs/operators';
import { environment } from '../../../../environments';
import {
  OrganizationManagementService
} from '../../zentility/_shared/_zen-legacy-common/zen-common-services/_services/organization-management.service';
import {
  BillImportMatchRequest,
  BillImportMatchResponse,
  BillImportRequest,
  BillImportResponse,
  MappedAccountNumbers
} from '../models/bill-import.models';
import { CommodityType } from '../../zentility/_shared/_zen-legacy-common/_models/commodity';

/**
 * Service for bill import matching and creation.
 *
 * Provides two main operations:
 * 1. match() - Takes extracted bill data and returns potential matches for existing entities
 * 2. import() - Creates entities and bills based on user-confirmed matches
 */
@Injectable({
  providedIn: 'root'
})
export class BillImportService {
  private baseUrl = environment.apiBaseUrl;

  constructor(
    private http: HttpClient,
    private orgMgtSvc: OrganizationManagementService
  ) {}

  /**
   * Match extracted bill data against existing entities.
   *
   * Sends grouped bill data to the API which returns:
   * - Potential matches for LENs, Service Addresses, and Meters
   * - Matched utilities and suppliers for each meter/bill
   * - Duplicate warnings for bills that overlap existing periods
   *
   * @param request Hierarchical structure of extracted bill data grouped by the UI
   * @returns Match suggestions for each entity level
   */
  match(request: BillImportMatchRequest): Observable<BillImportMatchResponse> {
    const url = `${this.baseUrl}/v4/organizations/${this.orgMgtSvc.getOrganizationId()}/bill-import/match`;
    return this.http.post<BillImportMatchResponse>(url, request).pipe(take(1));
  }

  /**
   * Import bills and create entities as needed.
   *
   * Takes the same hierarchical structure as match(), but with resolved IDs
   * from user's match selections. Entities with id=null will be created,
   * entities with an id will be reused.
   *
   * @param request Hierarchical structure with resolved IDs and full bill details
   * @returns Created entity IDs and import summary
   */
  import(request: BillImportRequest): Observable<BillImportResponse> {
    const url = `${this.baseUrl}/v4/organizations/${this.orgMgtSvc.getOrganizationId()}/bill-import`;
    return this.http.post<BillImportResponse>(url, request).pipe(take(1));
  }

  /**
   * Map identifiers to UAN positions for a given utility.
   * Uses the utility_account_number table (accountOrder + regex validation)
   * to determine which identifier goes in UAN1, UAN2, UAN3.
   *
   * Called when the user changes the utility on the meter step.
   */
  mapIdentifiers(request: { utilityId: number; commodityType?: CommodityType; additionalIdentifiers: Record<string, string> }):
      Observable<{ procurementAvailable: boolean; mappedAccountNumbers: MappedAccountNumbers }> {
    const url = `${this.baseUrl}/v4/organizations/${this.orgMgtSvc.getOrganizationId()}/bill-import/map-identifiers`;
    return this.http.post<{ procurementAvailable: boolean; mappedAccountNumbers: MappedAccountNumbers }>(url, request).pipe(take(1));
  }
}
